import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion, type Transition } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { cn } from "@shared/lib/cn";
import { nextScrollHideState, type ScrollHideState } from "@shared/lib/scrollHide";
import { useUiStore } from "@shared/stores/uiStore";
import { FloatingBackButton } from "@shared/ui/headers/FloatingBackButton";
import { SegmentedControl } from "@shared/ui/lists/SegmentedControl";
import { Button, type ButtonProps } from "@shared/ui/primitives/Button";

function normalizeHeaderTitle(title: ReactNode): ReactNode {
  if (typeof title === "string") {
    return title.replace(/\.\s*$/, "");
  }
  if (Array.isArray(title)) {
    return title.map((part, index) => <span key={index}>{normalizeHeaderTitle(part)}</span>);
  }
  return title;
}

/** Nearest scrollable ancestor of `el` (the page's scroll container), or null. */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    // Match the vertical scroll container by style so it's found even before its
    // content has grown enough to actually overflow (async-loaded screens).
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Collapse the header on scroll-down, reveal on scroll-up. Tracks the page's
 * scroll container and flips `collapsed` once past a small top threshold.
 */
function useScrollCollapse(ref: React.RefObject<HTMLElement>, enabled: boolean): boolean {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setCollapsed(false);
      return;
    }
    const scroller = getScrollParent(ref.current);
    if (!scroller) return;

    let state: ScrollHideState = { hidden: false, last: scroller.scrollTop };
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const next = nextScrollHideState(scroller.scrollTop, state);
        if (next.hidden !== state.hidden) setCollapsed(next.hidden);
        state = next;
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, enabled]);

  return collapsed;
}

/**
 * Collapse-on-scroll wiring shared by both headers: the ref to hang on the header
 * element, whether it is currently collapsed, and the slide transition to use.
 */
function useHeaderCollapse(enabled: boolean): {
  ref: React.RefObject<HTMLElement>;
  collapsed: boolean;
  transition: Transition;
} {
  const ref = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const collapsed = useScrollCollapse(ref, enabled);

  return {
    ref,
    collapsed,
    transition: reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 40 },
  };
}

function canNavigateBack(): boolean {
  if (typeof window === "undefined") return false;
  const idx = window.history.state?.idx;
  return typeof idx === "number" && idx > 0;
}

function navigateBackOrFallback(
  navigate: NavigateFunction,
  previousPath: string | null,
): void {
  if (canNavigateBack() && (previousPath === "/app/home" || previousPath === "/app/more")) {
    navigate(-1);
    return;
  }
  navigate("/app/home");
}

export interface HeroHeaderTab<T extends string> {
  value: T;
  label: string;
}

export interface HeroHeaderProps<T extends string = string> {
  eyebrow?: ReactNode;
  eyebrowSuffix?: ReactNode;
  title: ReactNode;
  showBack?: boolean;
  leading?: ReactNode;
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  backBehavior?: "contextual" | "parent";
  action?: ReactNode;
  tabs?: ReadonlyArray<HeroHeaderTab<T>>;
  activeTab?: T;
  onTabChange?: (value: T) => void;
  tabsLabel?: string;
  /** Pin the header to the top while the page scrolls. Default true; pages with
   *  their own scroll/layout (e.g. the home dashboard) can opt out. */
  sticky?: boolean;
  className?: string;
  contentClassName?: string;
  topRowClassName?: string;
  bodyClassName?: string;
  eyebrowClassName?: string;
  titleClassName?: string;
  /** Center the title between the back button and a balancing spacer. Default true;
   *  set false to fall back to the left-aligned title layout. */
  centerTitle?: boolean;
  /** Slide the header away on scroll-down (revealing it on scroll-up), leaving a
   *  floating back button. Defaults on for sticky back-button headers without
   *  tabs; pass false to keep the header always visible. */
  collapseOnScroll?: boolean;
  children?: ReactNode;
}

export interface HeroHeaderActionButtonProps extends Omit<
  ButtonProps,
  "children" | "size" | "aria-label"
> {
  icon: ReactNode;
  label: string;
}

/** Bottom-corner radius options for the compact header. */
export type CompactHeaderBottomRadius = "none" | "sm" | "md" | "lg" | "xl";

const COMPACT_HEADER_BOTTOM_RADIUS: Record<CompactHeaderBottomRadius, string> = {
  none: "rounded-b-none",
  sm: "rounded-b-sm",
  md: "rounded-b-md",
  lg: "rounded-b-lg",
  xl: "rounded-b-xl",
};

export interface CompactHeaderProps {
  title: ReactNode;
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  backBehavior?: "contextual" | "parent";
  action?: ReactNode;
  /** Rounding of the header's bottom corners. Defaults to "sm"; use "none" to sit flush against content below (e.g. a full-bleed image). */
  bottomRadius?: CompactHeaderBottomRadius;
  /** Slide the header away on scroll-down (revealing it on scroll-up), leaving a
   *  floating back button — the same behaviour as HeroHeader. Defaults on except
   *  where the header carries its own content below the title row. */
  collapseOnScroll?: boolean;
  /** Rendered inside the header, below the title row — e.g. the onboarding progress rail. */
  children?: ReactNode;
  className?: string;
}

export function HeroHeaderActionButton({
  icon,
  label,
  variant = "gold",
  className,
  ...props
}: HeroHeaderActionButtonProps): JSX.Element {
  return (
    <Button
      {...props}
      aria-label={label}
      variant={variant}
      size="icon"
      className={cn(
        "h-10 w-10 rounded-full shadow-sm",
        variant === "ghost" &&
          "border border-white/12 bg-white/15 text-white hover:bg-white/22 backdrop-blur",
        variant === "gold" && "hover:opacity-95",
        className,
      )}
    >
      {icon}
    </Button>
  );
}

export function CompactHeader({
  title,
  backTo = "/app/home",
  onBack,
  backLabel = "Back",
  backBehavior = "contextual",
  action,
  bottomRadius = "sm",
  collapseOnScroll,
  children,
  className,
}: CompactHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const previousPath = useUiStore((s) => s.previousPath);
  const displayTitle = normalizeHeaderTitle(title);
  // A header with its own body below the title row (e.g. the onboarding progress
  // rail) keeps that content on screen rather than sliding it away.
  const { ref: headerRef, collapsed, transition } = useHeaderCollapse(collapseOnScroll ?? !children);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backBehavior === "parent") {
      navigate(backTo);
      return;
    }
    navigateBackOrFallback(navigate, previousPath);
  };

  return (
    <>
    <motion.header
      ref={headerRef}
      animate={{ y: collapsed ? "-100%" : "0%" }}
      transition={transition}
      className={cn(
        "sticky top-0 z-30 overflow-hidden bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150",
        COMPACT_HEADER_BOTTOM_RADIUS[bottomRadius],
        "[background:color-mix(in_oklch,var(--primary-900)_88%,transparent)]",
        "shadow-[inset_0_-1px_0_rgba(255,255,255,0.10),0_12px_30px_-18px_rgba(0,0,0,0.55)]",
        className,
      )}
    >
      <div className="grid min-h-9 grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center gap-3">
        <Button
          type="button"
          onClick={handleBack}
          aria-label={backLabel}
          variant="ghost"
          size="icon"
          // The circle stays 36px so header height is unchanged, but an invisible
          // 4px collar takes the hit area to the 44px minimum tap target.
          // Opacity modifiers must land on Tailwind's 5-step scale — `/12` and `/18`
          // compile to nothing, which left the chip unfilled. With only a 1.34:1 ring
          // to see, the eye anchored on the arrow glyph (13px inside the button) and
          // read the header as indented against the 20px content column below.
          className="relative h-9 w-9 shrink-0 rounded-full border border-white/10 bg-white/15 text-white hover:bg-white/20 before:absolute before:-inset-1 before:content-['']"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        </Button>
        <h1 className="min-w-0 truncate text-center font-display text-body font-semibold leading-6 text-white">
          {displayTitle}
        </h1>
        {action ? <div className="shrink-0 justify-self-end">{action}</div> : <span aria-hidden />}
      </div>
      {children}
    </motion.header>
    <AnimatePresence>
      {collapsed ? (
        <FloatingBackButton key="compact-floating-back" onClick={handleBack} label={backLabel} />
      ) : null}
    </AnimatePresence>
    </>
  );
}

export function HeroHeader<T extends string = string>({
  eyebrowSuffix,
  title,
  showBack = true,
  leading,
  backTo = "/app/home",
  onBack,
  backLabel = "Back",
  backBehavior = "contextual",
  action,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel,
  sticky = true,
  className,
  contentClassName,
  topRowClassName,
  bodyClassName,
  eyebrowClassName,
  titleClassName,
  centerTitle = true,
  collapseOnScroll,
  children,
}: HeroHeaderProps<T>): JSX.Element {
  const navigate = useNavigate();
  const previousPath = useUiStore((s) => s.previousPath);
  const displayTitle = normalizeHeaderTitle(title);
  // Default on for the standard sticky back-button screens (not tab headers).
  const collapseEnabled = collapseOnScroll ?? (sticky && showBack && !leading && !tabs?.length);
  const { ref: headerRef, collapsed, transition } = useHeaderCollapse(collapseEnabled);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backBehavior === "parent") {
      navigate(backTo);
      return;
    }
    navigateBackOrFallback(navigate, previousPath);
  };
  const leadingContent =
    leading ??
    (showBack ? (
      <Button
        type="button"
        onClick={handleBack}
        aria-label={backLabel}
        variant="ghost"
        size="icon"
        className="h-10 w-10 shrink-0 rounded-full border border-white/10 bg-white/12 text-white hover:bg-white/18"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
      </Button>
    ) : null);

  return (
    <>
    <motion.header
        ref={headerRef}
        animate={{ y: collapsed ? "-100%" : "0%" }}
        transition={transition}
        className={cn(
          // The hero stays pinned to the top while the page scrolls beneath it.
          // Pages with their own scroll/layout opt out with `sticky={false}`.
          sticky ? "sticky top-0 z-30" : "relative",
          "overflow-hidden bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150",
          "[background:color-mix(in_oklch,var(--primary-900)_70%,transparent)]",
          "shadow-[inset_0_-1px_0_rgba(255,255,255,0.14),0_12px_32px_-28px_rgba(0,0,0,0.55)]",
          className,
        )}
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-white/18 via-black/8 to-black/32 backdrop-blur-sm backdrop-saturate-150"
        />
        <div className={cn("relative", contentClassName)}>
          <div className={bodyClassName}>
            {centerTitle ? (
              // Single compact nav row: the leading cluster (custom menu/pills or the
              // back button) and the action cluster sit on the SAME line as the
              // centered title — no separate title band below the icons, no vertical
              // gap. Equal-fr side columns keep the title at the true horizontal centre
              // of the header regardless of the leading/action cluster widths — the wide
              // property pill no longer pushes it right. When the pill is wider than a
              // fair half-gutter the title shifts the minimum needed to sit flush against
              // it (never overlapping) and truncates before it can collide with either
              // side. Fixed 2.5rem side columns are kept for the back-button branch, and
              // an empty title collapses the middle column, leaving just the icons on one row.
              <div
                className={cn(
                  "grid items-center gap-3",
                  leading
                    ? "grid-cols-[1fr_auto_1fr]"
                    : "grid-cols-[2.5rem_minmax(0,1fr)_2.5rem]",
                  topRowClassName,
                )}
              >
                {leadingContent ?? <span aria-hidden />}
                <div className="min-w-0 text-center">
                  {eyebrowSuffix ? (
                    <div
                      className={cn(
                        "flex flex-wrap items-center justify-center gap-2",
                        eyebrowClassName,
                      )}
                    >
                      {eyebrowSuffix}
                    </div>
                  ) : null}
                  <h1
                    className={cn(
                      "truncate display-serif text-display-lg leading-tight text-white",
                      titleClassName,
                    )}
                  >
                    {displayTitle}
                  </h1>
                </div>
                {action ? (
                  <div className="shrink-0 justify-self-end">{action}</div>
                ) : (
                  <span aria-hidden />
                )}
              </div>
            ) : (
              <div
                className={cn(
                  leading
                    ? "mt-3 flex items-center gap-3"
                    : "mt-1 flex items-center gap-3",
                )}
              >
                {showBack && !leading ? leadingContent : null}
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="min-w-0 flex-1">
                    {eyebrowSuffix ? (
                      <div
                        className={cn(
                          "flex flex-wrap items-center gap-2",
                          eyebrowClassName,
                        )}
                      >
                        {eyebrowSuffix}
                      </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2">
                      <h1
                        className={cn(
                          "display-serif text-display-lg leading-tight text-white",
                          titleClassName,
                        )}
                      >
                        {displayTitle}
                      </h1>
                    </div>
                  </div>
                  {action && !leading ? (
                    <div className="shrink-0">{action}</div>
                  ) : null}
                </div>
              </div>
            )}
            {children}
            {tabs?.length && activeTab && onTabChange ? (
              <SegmentedControl
                options={tabs}
                value={activeTab}
                onChange={onTabChange}
                ariaLabel={tabsLabel}
                fullWidth
                tone="inverse"
                className="mt-6"
              />
            ) : null}
          </div>
        </div>
    </motion.header>
    <AnimatePresence>
      {collapsed ? (
        <FloatingBackButton key="hero-floating-back" onClick={handleBack} label={backLabel} />
      ) : null}
    </AnimatePresence>
    </>
  );
}
